import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import MainLayout from '../../components/layout/MainLayout';
import PortfolioChart from './components/PortfolioChart';
import AssetBalanceCard from './components/AssetBalanceCard';
import QuickActionButtons from './components/QuickActionButtons';
import ActivityFeed from './components/ActivityFeed';

import WalletConnectionPanel from './components/WalletConnectionPanel';
import LinkedCardsManager from './components/LinkedCardsManager';
import TransactionModal from './components/TransactionModal';
import PortfolioAnalytics from './components/PortfolioAnalytics';
import SecurityMonitor from './components/SecurityMonitor';
import DeFiPositions from './components/DeFiPositions';
import Icon from '../../components/AppIcon';



import portfolioService from '../../services/portfolioService';
import transactionService from '../../services/transactionService';
import defiService from '../../services/defiService';
import linkedCardService from '../../services/linkedCardService';

const UserWalletDashboard = () => {
  const { userProfile } = useAuth();
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [totalBalance, setTotalBalance] = useState(0);
  const [balanceChange, setBalanceChange] = useState(0);
  const [selectedAsset, setSelectedAsset] = useState('all');
  const [recentNotifications, setRecentNotifications] = useState([]);
  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);
  const [connectedWallets, setConnectedWallets] = useState({
    ethereum: null,
    solana: null
  });
  const [assetBalances, setAssetBalances] = useState([]);
  const [linkedCards, setLinkedCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Fetch multi-chain portfolio
  const { data: portfolioData, refetch: refetchPortfolio } = useQuery({
    queryKey: ['portfolio', connectedWallets],
    queryFn: () => portfolioService?.getMultiChainPortfolio(connectedWallets),
    enabled: !!(connectedWallets?.ethereum || connectedWallets?.solana),
    refetchInterval: 30000
  });

  // Fetch portfolio performance
  const { data: performanceData } = useQuery({
    queryKey: ['performance', connectedWallets],
    queryFn: () => portfolioService?.getPortfolioPerformance(connectedWallets),
    enabled: !!(connectedWallets?.ethereum || connectedWallets?.solana),
    refetchInterval: 60000
  });

  // Fetch transaction history
  const { data: transactions } = useQuery({
    queryKey: ['transactions', connectedWallets],
    queryFn: () => transactionService?.getAllTransactions(connectedWallets, 20),
    enabled: !!(connectedWallets?.ethereum || connectedWallets?.solana),
    refetchInterval: 30000
  });

  // Fetch DeFi opportunities
  const { data: defiOpportunities } = useQuery({
    queryKey: ['defi-opportunities'],
    queryFn: () => defiService?.getStakingOpportunities('all'),
    refetchInterval: 300000
  });

  // Fetch DeFi positions
  const { data: defiPositions } = useQuery({
    queryKey: ['defi-positions', connectedWallets],
    queryFn: () => defiService?.getDeFiPortfolioSummary(connectedWallets?.ethereum),
    enabled: !!connectedWallets?.ethereum,
    refetchInterval: 60000
  });

  useEffect(() => {
    if (userProfile?.id) {
      loadDashboardData();
    }
  }, [userProfile?.id]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [assetsData, cardsData] = await Promise.all([
        portfolioService?.getAssets(userProfile?.id),
        linkedCardService?.getAll(userProfile?.id)
      ]);
      setAssetBalances(assetsData);
      setLinkedCards(cardsData);
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Calculate total linked cards balance
  const linkedCardsBalance = linkedCards?.reduce((total, card) => {
    // For now, we'll show the active card or first card
    // In a real app, this would come from actual card balance API
    return total;
  }, 0);

  // Combine crypto assets with linked card data
  const allAssets = [
    ...assetBalances,
    ...(linkedCards?.length > 0 ? linkedCards?.map(card => ({
      id: card?.id,
      name: `${card?.cardName} •••• ${card?.cardNumberLastFour}`,
      symbol: card?.cardBrand?.toUpperCase(),
      balance: 0, // Card balance would come from payment processor API
      usdValue: 0,
      change24h: 0.0,
      icon: 'CreditCard',
      color: '#8B5CF6',
      allocation: 0,
      isCard: true,
      cardData: card
    })) : [])
  ];

  // Update portfolio data when fetched
  useEffect(() => {
    if (portfolioData?.assets) {
      setAssetBalances(portfolioData?.assets);
      setTotalBalance(parseFloat(portfolioData?.totalValue?.toString()));
      setLastUpdate(new Date());
    }
  }, [portfolioData]);

  // Calculate weighted balance change
  useEffect(() => {
    if (assetBalances?.length > 0) {
      const weightedChange = assetBalances?.reduce((sum, asset) => {
        return sum + (asset?.change24h * (asset?.allocation / 100));
      }, 0);
      setBalanceChange(weightedChange);
    }
  }, [assetBalances]);

  const handleTransactionSuccess = (result) => {
    refetchPortfolio();
    // Show success notification
  };

  const handleWalletConnect = (walletType, address) => {
    setConnectedWallets(prev => ({
      ...prev,
      [walletType]: address
    }));
  };

  // Add this block - handler for quick action clicks
  const handleActionClick = (action) => {
    if (action === 'send' || action === 'receive' || action === 'swap' || action === 'buy') {
      setIsTransactionModalOpen(true);
    }
  };

  return (
    <MainLayout>
      <div className="dashboard-container">
        <div className="dashboard-header">
          <div>
            <h1 className="dashboard-title">Wallet Dashboard</h1>
            <p className="dashboard-subtitle">Manage your crypto assets and transactions</p>
          </div>
          <div className="flex items-center gap-4">
            {userProfile?.tier === 'pro' && (
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/20 text-primary rounded-lg">
                <Icon name="Crown" size={20} />
                <span className="font-semibold">Pro Account</span>
              </div>
            )}
            <div className="stat-badge">
              <Icon name="Clock" size={16} className="text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                Updated {lastUpdate?.toLocaleTimeString()}
              </span>
            </div>
          </div>
        </div>

        <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto">
          {/* Total Balance Section */}
          <div className="card mb-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-sm text-muted-foreground mb-2">Total Portfolio Value</p>
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-2">
                  ${totalBalance?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </h2>
                <div className="flex items-center gap-2">
                  <Icon 
                    name={balanceChange >= 0 ? 'TrendingUp' : 'TrendingDown'} 
                    size={16} 
                    className={balanceChange >= 0 ? 'text-success' : 'text-error'}
                  />
                  <span className={`text-sm font-semibold ${balanceChange >= 0 ? 'text-success' : 'text-error'}`}>
                    {balanceChange >= 0 ? '+' : ''}{balanceChange?.toFixed(2)}%
                  </span>
                  <span className="text-sm text-muted-foreground">24h change</span>
                </div>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1">
                  <Icon name="Radio" size={12} className="text-success" />
                  <span>Live Prices</span>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <QuickActionButtons onActionClick={handleActionClick} />

          {/* Web3 Wallet Connection */}
          <div className="mb-6">
            <WalletConnectionPanel />
          </div>

          {/* Linked Cards Manager */}
          <div className="mb-6">
            <LinkedCardsManager />
          </div>

          {/* Portfolio Analytics */}
          {performanceData && (
            <div className="mb-6">
              <PortfolioAnalytics 
                performance={performanceData} 
                assets={assetBalances}
              />
            </div>
          )}

          {/* Asset Balance Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {allAssets?.map((asset) => (
              <AssetBalanceCard key={asset?.id || asset?.symbol} asset={asset} />
            ))}
          </div>

          {/* Charts and Security */}
          <div className="grid lg:grid-cols-2 gap-6 mb-6">
            <PortfolioChart assets={assetBalances} />
            <SecurityMonitor 
              walletAddress={connectedWallets?.ethereum || connectedWallets?.solana}
              onSettingsClick={() => window.location.href = '/two-factor-authentication-management'}
            />
          </div>

          {/* DeFi Positions */}
          {defiPositions && defiOpportunities && (
            <div className="mb-6">
              <DeFiPositions
                positions={defiPositions}
                opportunities={defiOpportunities}
                onStake={() => console.log('Stake clicked')}
                onUnstake={() => console.log('Unstake clicked')}
              />
            </div>
          )}

          {/* Activity Feed */}
          <div className="mb-6">
            <ActivityFeed activities={transactions} />
          </div>
        </div>
      </div>

      {/* Transaction Modal */}
      <TransactionModal
        isOpen={isTransactionModalOpen}
        onClose={() => setIsTransactionModalOpen(false)}
        wallets={connectedWallets}
        onSuccess={handleTransactionSuccess}
      />
    </MainLayout>
  );
};

export default UserWalletDashboard;